import { useCallback, useMemo, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';
import { useResourceList } from '@/spaces/people/useResourceList';
import type { ParsedContactRow } from '@/utils/contactsCsv';
import { trackEvent } from '@/utils/fullstory';

import type { Contact, ContactPatch, PaginatedContactsResponse } from './types';

type CreateContactRequest = Pick<
	Contact,
	'name' | 'email' | 'phone' | 'instagram_handle'
>;

const CONTACTS_PAGE_SIZE = 50;

interface UseContactsTabArgs {
	selectedContactId: string | null;
	onSelectContact: (id: string | null) => void;
	draftContact: Contact | null;
	onDraftChange: (draft: Contact | null) => void;
	onAudienceMemberCountDelta?: (audienceId: string, delta: number) => void;
}

export const useContactsTab = ({
	selectedContactId,
	onSelectContact,
	draftContact,
	onDraftChange,
	onAudienceMemberCountDelta,
}: UseContactsTabArgs) => {
	const bridgeApi = useBridgeApi();
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();

	const [isSavingContact, setIsSavingContact] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);

	const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(
		() => new Set(),
	);
	const [bulkError, setBulkError] = useState<string | null>(null);
	const [isBulkSaving, setIsBulkSaving] = useState(false);

	const clearSelection = useCallback(
		() => setSelectedContactIds(new Set()),
		[],
	);

	const paginatedPath = useCallback(
		(uid: string, params: URLSearchParams) =>
			`/api/user/${encodeURIComponent(uid)}/contacts/paginated?${params.toString()}`,
		[],
	);
	const detailPath = useCallback(
		(uid: string, id: string) =>
			`/api/user/${encodeURIComponent(uid)}/contacts/${encodeURIComponent(id)}`,
		[],
	);
	const searchPath = useCallback(
		(uid: string, params: URLSearchParams) =>
			`/api/user/${encodeURIComponent(uid)}/contacts/search?${params.toString()}`,
		[],
	);
	const extractItems = useCallback(
		(response: PaginatedContactsResponse) => response.contacts,
		[],
	);
	const getId = useCallback((contact: Contact) => contact.id, []);
	const errorMessages = useMemo(
		() => ({
			listLoadFailed: 'Failed to load contacts. Please try again.',
			itemLoadFailed: 'Contact not found or failed to load.',
		}),
		[],
	);

	const list = useResourceList<Contact, PaginatedContactsResponse>({
		userId,
		selectedId: selectedContactId,
		pageSize: CONTACTS_PAGE_SIZE,
		logLabel: 'contacts',
		paginatedPath,
		detailPath,
		searchPath,
		extractItems,
		getId,
		errorMessages,
	});

	const mutateContactAudienceIds = useCallback(
		(contactId: string, mutator: (ids: string[]) => string[]) => {
			const current =
				list.items.find((c) => c.id === contactId) ??
				list.searchResults?.find((c) => c.id === contactId);
			if (!current) return;
			list.updateItem({
				...current,
				audience_ids: mutator(current.audience_ids ?? []),
			});
		},
		[list],
	);

	const runBulkMembership = useCallback(
		async (audienceId: string, status: 'active' | 'removed') => {
			const ids = Array.from(selectedContactIds);
			if (ids.length === 0) return;
			setIsBulkSaving(true);
			setBulkError(null);
			const priorAudienceIds = new Map<string, string[]>();
			for (const contactId of ids) {
				const contact =
					list.items.find((c) => c.id === contactId) ??
					list.searchResults?.find((c) => c.id === contactId);
				priorAudienceIds.set(contactId, contact?.audience_ids ?? []);
			}
			const failures: string[] = [];
			const successes: string[] = [];
			await Promise.all(
				ids.map(async (contactId) => {
					try {
						await bridgewayApi.put(
							`/api/user/${encodeURIComponent(userId)}/audiences/${encodeURIComponent(audienceId)}/members/${encodeURIComponent(contactId)}`,
							{ status, owner: 'user' },
						);
						successes.push(contactId);
					} catch (err) {
						logger.error('Failed to update audience membership', {
							error: err,
							audienceId,
							contactId,
							status,
						});
						failures.push(contactId);
					}
				}),
			);
			let memberDelta = 0;
			for (const contactId of successes) {
				const wasMember = (priorAudienceIds.get(contactId) ?? []).includes(
					audienceId,
				);
				if (status === 'active' && !wasMember) memberDelta += 1;
				else if (status === 'removed' && wasMember) memberDelta -= 1;
				mutateContactAudienceIds(contactId, (current) =>
					status === 'active'
						? current.includes(audienceId)
							? current
							: [...current, audienceId]
						: current.filter((id) => id !== audienceId),
				);
			}
			if (memberDelta !== 0) {
				onAudienceMemberCountDelta?.(audienceId, memberDelta);
			}
			setIsBulkSaving(false);
			if (failures.length > 0) {
				const verb = status === 'active' ? 'add' : 'remove';
				setBulkError(
					`Couldn't ${verb} ${failures.length} contact${failures.length === 1 ? '' : 's'}.`,
				);
				setSelectedContactIds(new Set(failures));
			} else {
				setSelectedContactIds(new Set());
			}
		},
		[
			bridgewayApi,
			list,
			mutateContactAudienceIds,
			onAudienceMemberCountDelta,
			selectedContactIds,
			userId,
		],
	);

	const handleBulkAddToAudience = useCallback(
		(audienceId: string) => {
			trackEvent('Contacts Bulk Added To Audience', {
				audience_id: audienceId,
				contact_count: selectedContactIds.size,
			});
			return runBulkMembership(audienceId, 'active');
		},
		[runBulkMembership, selectedContactIds],
	);
	const handleBulkRemoveFromAudience = useCallback(
		(audienceId: string) => {
			trackEvent('Contacts Bulk Removed From Audience', {
				audience_id: audienceId,
				contact_count: selectedContactIds.size,
			});
			return runBulkMembership(audienceId, 'removed');
		},
		[runBulkMembership, selectedContactIds],
	);

	const handleImportCsv = useCallback(
		async (rows: ParsedContactRow[]): Promise<{ savedCount: number }> => {
			if (!userId || rows.length === 0) return { savedCount: 0 };
			await bridgewayApi.post<{
				id: string;
				status_code: number;
				message: string;
			}>(`/api/user/${encodeURIComponent(userId)}/contacts/`, {
				contacts: rows,
				source: 'csv',
			});
			trackEvent('Contacts CSV Imported', { contact_count: rows.length });
			await list.refresh();
			return { savedCount: rows.length };
		},
		[bridgewayApi, list, userId],
	);

	const handleContactClick = useCallback(
		(contact: Contact) => {
			trackEvent('Contact Detail Viewed', { contact_id: contact.id });
			onSelectContact(contact.id);
			bridgeApi.sendAction({
				type: 'do_navigate_to_contact_detail',
				contact_id: contact.id,
			});
		},
		[bridgeApi, onSelectContact],
	);

	const handleClosePanel = useCallback(() => {
		if (draftContact) {
			onDraftChange(null);
			setSaveError(null);
			return;
		}
		onSelectContact(null);
		bridgeApi.sendAction({
			type: 'do_navigate_to_contact_detail',
			contact_id: null,
		});
	}, [bridgeApi, draftContact, onDraftChange, onSelectContact]);

	const handleContactSave = useCallback(
		async (updates: ContactPatch) => {
			if (!userId || !selectedContactId) return;

			setIsSavingContact(true);
			setSaveError(null);
			try {
				const updated = await bridgewayApi.patch<Contact>(
					`/api/user/${encodeURIComponent(userId)}/contacts/${encodeURIComponent(selectedContactId)}`,
					updates,
				);
				list.updateItem(updated);
				trackEvent('Contact Updated', { contact_id: updated.id });
			} catch (err) {
				logger.error('Failed to update contact', { error: err });
				const message =
					err instanceof Error ? err.message : 'Failed to update contact';
				setSaveError(message);
				throw err;
			} finally {
				setIsSavingContact(false);
			}
		},
		[bridgewayApi, list, selectedContactId, userId],
	);

	const handleContactDelete = useCallback(async () => {
		if (!userId || !selectedContactId) return;

		try {
			await bridgewayApi.delete(
				`/api/user/${encodeURIComponent(userId)}/contacts/${encodeURIComponent(selectedContactId)}`,
			);
			trackEvent('Contact Deleted', { contact_id: selectedContactId });
			list.removeItem(selectedContactId);
			onSelectContact(null);
			bridgeApi.sendAction({
				type: 'do_navigate_to_contact_detail',
				contact_id: null,
			});
		} catch (err) {
			logger.error('Failed to delete contact', { error: err });
			setSaveError('Failed to delete contact. Please try again.');
		}
	}, [
		bridgeApi,
		bridgewayApi,
		list,
		onSelectContact,
		selectedContactId,
		userId,
	]);

	const handleCreate = useCallback(() => {
		onDraftChange({
			id: '',
			user_id: userId,
			source: 'manual',
			name: '',
			email: '',
			phone: '',
			instagram_handle: '',
		});
		setSaveError(null);
	}, [onDraftChange, userId]);

	const handleDraftSave = useCallback(
		async (updates: ContactPatch) => {
			if (!userId) return;

			setIsSavingContact(true);
			setSaveError(null);
			try {
				const body: CreateContactRequest = {
					name: updates.name ?? draftContact?.name ?? null,
					email: updates.email ?? draftContact?.email ?? null,
					phone: updates.phone ?? draftContact?.phone ?? null,
					instagram_handle:
						updates.instagram_handle ?? draftContact?.instagram_handle ?? null,
				};
				const created = await bridgewayApi.post<Contact>(
					`/api/user/${encodeURIComponent(userId)}/contacts/create`,
					body,
				);
				list.prependItem(created);
				onDraftChange(null);
				onSelectContact(created.id);
				trackEvent('Contact Created', { contact_id: created.id });
				bridgeApi.sendAction({
					type: 'do_navigate_to_contact_detail',
					contact_id: created.id,
				});
			} catch (err) {
				logger.error('Failed to create contact', { error: err });
				const message =
					err instanceof Error ? err.message : 'Failed to create contact';
				setSaveError(message);
				throw err;
			} finally {
				setIsSavingContact(false);
			}
		},
		[
			bridgeApi,
			bridgewayApi,
			draftContact,
			list,
			onDraftChange,
			onSelectContact,
			userId,
		],
	);

	return {
		list,
		isSavingContact,
		saveError,
		handleContactClick,
		handleClosePanel,
		handleContactSave,
		handleContactDelete,
		handleCreate,
		handleDraftSave,
		handleImportCsv,
		selectedContactIds,
		setSelectedContactIds,
		clearSelection,
		bulkError,
		isBulkSaving,
		handleBulkAddToAudience,
		handleBulkRemoveFromAudience,
	};
};
